import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@akaike/ui";
import { IconCollapse, IconExpand } from "./icons";

// Click-to-pick map for a single brand-search probe point. The lat/lon number
// inputs on the ProbeLocations page stay the source of truth; this two-way-binds to
// them: click the map (or drag the pin) -> the inputs update; type into the inputs ->
// the pin follows. Raw Leaflet (the only map dep, mirrors BboxMapPicker). Existing
// points are drawn as faint context markers so an operator can see the current spread
// while placing a new one.

export type LatLon = { lat: number; lon: number };
export type ContextPoint = {
  id: string;
  lat: number;
  lon: number;
  label: string;
  enabled: boolean;
};

const INDIA: L.LatLngTuple = [22.6, 80.0];
const BRAND = "#c2870f";

const r = (n: number) => Math.round(n * 1e5) / 1e5; // ~1 m precision

const PIN_ICON = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;background:${BRAND};border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.5);cursor:move"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function contextIcon(enabled: boolean): L.DivIcon {
  const bg = enabled ? "#3b82f6" : "#9ca3af";
  return L.divIcon({
    className: "",
    html: `<div style="width:10px;height:10px;background:${bg};border:1.5px solid #fff;border-radius:50%;opacity:.65"></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  });
}

export function ProbePointPicker({
  value,
  context = [],
  excludeId,
  onPick,
}: {
  value: LatLon | null;
  context?: ContextPoint[];
  excludeId?: string | null;
  onPick: (v: LatLon) => void;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const pinRef = useRef<L.Marker | null>(null);
  const ctxRef = useRef<L.LayerGroup | null>(null);
  // True while emitting our own edit, so the value-prop effect doesn't fight a drag.
  const selfEditRef = useRef(false);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const [expanded, setExpanded] = useState(false);

  function emit(lat: number, lon: number): void {
    selfEditRef.current = true;
    onPickRef.current({ lat: r(lat), lon: r(lon) });
  }

  function setPin(lat: number, lon: number): void {
    const map = mapRef.current;
    if (!map) return;
    if (!pinRef.current) {
      const m = L.marker([lat, lon], {
        icon: PIN_ICON,
        draggable: true,
        zIndexOffset: 1000,
      }).addTo(map);
      m.on("drag", () => {
        const p = m.getLatLng();
        emit(p.lat, p.lng);
      });
      pinRef.current = m;
    } else {
      pinRef.current.setLatLng([lat, lon]);
    }
  }

  // -- create the map once -----------------------------------------------------
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      attributionControl: false,
      worldCopyJump: true,
    }).setView(value ? [value.lat, value.lon] : INDIA, value ? 12 : 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) => {
      setPin(e.latlng.lat, e.latlng.lng);
      emit(e.latlng.lat, e.latlng.lng);
    });
    ctxRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    if (value) setPin(value.lat, value.lon);
    const t0 = setTimeout(() => map.invalidateSize(), 0);
    const t1 = setTimeout(() => map.invalidateSize(), 200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      map.remove();
      mapRef.current = null;
      pinRef.current = null;
      ctxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- follow the inputs when THEY change (not our own drag/click) -------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (selfEditRef.current) {
      selfEditRef.current = false;
      return;
    }
    if (value) {
      setPin(value.lat, value.lon);
      // Recenter/zoom to the point when it wouldn't be clearly visible: e.g. clicking
      // Edit from the India overview. Skip the jump when we're already zoomed in and the
      // point sits comfortably in view (so manual lat/lon typing doesn't yank the map).
      const inView = map.getBounds().pad(-0.25).contains([value.lat, value.lon]);
      if (map.getZoom() < 12 || !inView) {
        map.flyTo([value.lat, value.lon], Math.max(map.getZoom(), 13), {
          duration: 0.5,
        });
      }
    } else {
      pinRef.current?.remove();
      pinRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lon]);

  // -- redraw context markers when the point set changes -----------------------
  useEffect(() => {
    const group = ctxRef.current;
    if (!group) return;
    group.clearLayers();
    for (const p of context) {
      if (excludeId && p.id === excludeId) continue;
      L.marker([p.lat, p.lon], {
        icon: contextIcon(p.enabled),
        interactive: true,
      })
        .bindTooltip(p.label, { direction: "top" })
        .addTo(group);
    }
  }, [context, excludeId]);

  // The container resized (inline <-> overlay): let Leaflet re-read its size.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(t);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [expanded]);

  return (
    <>
      <div
        onClick={() => setExpanded(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.5)",
          zIndex: 3900,
          display: expanded ? "block" : "none",
        }}
      />
      <div
        className="col"
        style={
          expanded
            ? {
                position: "fixed",
                inset: 32,
                zIndex: 4000,
                gap: 8,
                padding: 12,
                background: "var(--panel)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                boxShadow: "0 12px 48px rgba(0,0,0,.45)",
              }
            : { gap: 8 }
        }
      >
        <div
          style={{
            position: "relative",
            flex: expanded ? 1 : "none",
            minHeight: 0,
          }}
        >
          <div
            ref={elRef}
            style={{
              width: "100%",
              height: expanded ? "100%" : 280,
              borderRadius: 6,
              overflow: "hidden",
              zIndex: 0,
            }}
          />
          <Button
            size="sm"
            type="button"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Collapse map" : "Expand map for precise placement"}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 500,
              boxShadow: "0 2px 8px rgba(0,0,0,.3)",
            }}
          >
            {expanded ? <IconCollapse /> : <IconExpand />}
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </div>
        <span className="hint">
          Click the map to drop the probe point, or drag the pin. The lat/lon fields stay in sync.
          Faint dots are the other probe points.
        </span>
      </div>
    </>
  );
}
